/**
 * Format a date string to a more readable format
 * @param dateString ISO date string to format
 * @param options Optional formatting options
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string,
  options: { 
    includeTime?: boolean 
  } = {}
): string => {
  try {
    if (!dateString) return '';
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return dateString; // Return the original string if invalid date
    }
    
    // Format options
    const dateFormatOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      ...(options.includeTime ? { hour: '2-digit', minute: '2-digit' } : {})
    };
    
    return date.toLocaleDateString('fr-FR', dateFormatOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format a date for input fields (yyyy-MM-dd)
 * @param dateString ISO date string to format
 * @returns Date string in yyyy-MM-dd format
 */
export const formatDateForInput = (dateString: string): string => {
  try {
    if (!dateString) return '';
    const date = new Date(dateString);
    return formatDateToYYYYMMDD(date);
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return dateString;
  }
};

/**
 * Get the first day of the previous month
 * @returns ISO date string for the first day of the previous month
 */
export const getFirstDayOfPreviousMonth = (): string => {
  const today = new Date();
  const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return previousMonth.toISOString();
};

/**
 * Format a date to YYYY-MM-DD format
 * @param date The date to format
 * @returns The formatted date string
 */
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a date in relative terms (e.g., "il y a 3 jours")
 * @param dateString ISO date string to format
 * @returns Relative formatted date string
 */
export const formatRelativeDate = (dateString: string): string => {
  try {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 1) {
      return 'Aujourd\'hui';
    } else if (diffInDays === 1) {
      return 'Hier';
    } else if (diffInDays < 30) {
      return `Il y a ${diffInDays} jours`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `Il y a ${months} mois`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return `Il y a ${years} an${years > 1 ? 's' : ''}`;
    }
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return dateString;
  }
};

/**
 * Format a date string to YYYY-MM-DD format
 * @param dateString Date string in any format that Date can parse
 * @returns Formatted date string
 */
export const formatDateISO = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return '';
    }
    
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error formatting date to ISO:', error);
    return '';
  }
}; 